import { ReactNode, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DashboardCard } from '../widgets/DashboardCard'
import { WidgetMenu, WidgetMenuItem } from '../widgets/WidgetMenu'
import { runQuery } from '../engine'
import { frameForConfig } from '../frames'
import { DashboardTile as Tile } from '../dashboards'
import { resolveThreshold, useKpis } from '../kpis'
import { listSavedDashboards } from '../saved-dashboards'
import { getSavedView } from '../saved-views'
import { getTemplate } from '../templates'
import { QueryResult, ViewConfig } from '../view-config'
import { SourceBadge } from './LibraryTable'
import { DashboardTileChart } from './DashboardTileChart'
import { MetricView } from './MetricView'
import { RankedListView } from './RankedListView'
import { ReportChart, ReportChartType } from './ReportChart'
import { ReportTable } from './ReportTable'
import {
  availableViews,
  resolveView,
  ReportView,
  ViewSwitcher,
} from './ViewSwitcher'

/**
 * Kebab actions on a dashboard tile. Most are placeholder handlers for the
 * prototype (backend wired later); "Edit" opens the tile's underlying report in
 * the builder (edit mode). "Add to…" drills into the user's dashboards so a
 * widget can be dropped onto another dashboard.
 *
 * @param onEdit - open the underlying report in edit mode (see DashboardTileCard)
 */
function tileMenuItems(onEdit: () => void): WidgetMenuItem[] {
  const dashboards = listSavedDashboards()
  return [
    {
      label: 'Add to…',
      submenu:
        dashboards.length > 0
          ? dashboards.map((d) => ({ label: d.name }))
          : [{ label: 'No dashboards yet' }],
    },
    { label: 'Export as image' },
    { label: 'Pin to homepage' },
    { label: 'Edit', onSelect: onEdit },
  ]
}

/**
 * The live renderable config for a tile. The tile stores a config snapshot from
 * when it was added; this resolves the report as it is *now* so an edit shows on
 * the tile — pipeline reports recompile their op-chain (like the detail page),
 * flat reports are a ViewConfig already. Falls back to the snapshot if the
 * report was deleted. Used for both the tile body and the builder's view picker
 * so they never disagree about the report's shape.
 */
export function resolveTileConfig(tile: Tile): ViewConfig {
  const saved = getSavedView(tile.config.id)
  return saved ?? getTemplate(tile.config.id) ?? tile.config
}

/**
 * The view a tile shows: its saved per-tile override when that override is valid
 * for the report's shape, else the report's own default view — the same resolver
 * the detail page uses, so a tile reads as the report does (a report saved as a
 * line shows as a line on the dashboard, not silently demoted to a bar).
 *
 * @param tile - the dashboard tile (its `view` is the optional override)
 * @param config - the tile's report config
 * @param result - the computed result (drives which views are valid)
 * @returns the ReportView to render on the tile
 */
export function effectiveTileView(
  tile: Tile,
  config: ViewConfig,
  result: QueryResult
): ReportView {
  // Only honor the per-tile override while it still fits the report's shape; a
  // stale override (shape changed under it) falls through to the report's own
  // default view, same as the detail page.
  const valid = availableViews(config, result).some((v) => v.view === tile.view)
  return resolveView(config, result, valid ? (tile.view ?? null) : null)
}

/**
 * Per-tile "show as" switcher — how this report is displayed *on this dashboard*.
 * The same segmented PillTabs control the report detail page uses (so the two
 * read identically), offering the views valid for the report's shape; the choice
 * is saved on the tile and never touches the report's own view. Renders nothing
 * when the report shape allows only one view. Used on the builder canvas.
 */
export function TileViewTabs({
  tile,
  onChange,
}: {
  tile: Tile
  onChange: (view: ReportView) => void
}) {
  const liveConfig = resolveTileConfig(tile)
  const result = useMemo(
    () => runQuery(liveConfig, frameForConfig(liveConfig)),
    [liveConfig]
  )
  const view = effectiveTileView(tile, liveConfig, result)
  return (
    <ViewSwitcher
      config={liveConfig}
      result={result}
      view={view}
      onChange={onChange}
    />
  )
}

/**
 * Renders a tile's body in a given view, reusing the report's own renderers so a
 * tile reads the same as the report detail page. bar/stacked keep the dashboard
 * tile chart treatment; the other encodings reuse ReportChart.
 */
function TileBody({
  config,
  result,
  view,
}: {
  config: ViewConfig
  result: QueryResult
  view: ReportView
}) {
  if (view === 'metric')
    return <MetricView config={config} result={result} fill />
  if (view === 'ranked')
    return <RankedListView config={config} result={result} />
  if (view === 'table')
    // A raw list is a sortable table (it isn't aggregated, so magnitude bars
    // would misrepresent it); a grouped summary uses the few-row summary table.
    return config.kind === 'list' ? (
      <ReportTable config={config} result={result} fill sortable />
    ) : (
      <ReportTable config={config} result={result} />
    )
  if (view === 'bar' || view === 'stacked-bar')
    return (
      <DashboardTileChart
        variant={view === 'stacked-bar' ? 'stacked' : 'bar'}
        config={config}
        result={result}
      />
    )
  // horizontal-bar / line / dot / grouped-bar — reuse the full chart renderer,
  // bare and filling the tile (fixed height would clip the axis in a short tile).
  return (
    <ReportChart
      bare
      fill
      chartType={view as ReportChartType}
      config={config}
      result={result}
    />
  )
}

/**
 * One dashboard tile: compact chart or mini-table, whole card clickable
 * through to the full report (knobs live there, not on the tile). The tile's
 * `view` override decides how the report is shown here without touching the
 * report's own saved view.
 */
export function DashboardTileCard({
  tile,
  linkTo,
  action,
}: {
  tile: Tile
  /**
   * Card link target. Defaults to the tile's report detail page; pass `null` to
   * disable navigation (e.g. on the dashboard builder canvas, where clicking a
   * tile shouldn't leave the page).
   */
  linkTo?: string | null
  /** Override the header action slot (defaults to the tile kebab menu). */
  action?: ReactNode
}) {
  const { config } = tile
  const navigate = useNavigate()
  const { kpis } = useKpis()
  // The tile's stored config is a snapshot from when it was added; resolve the
  // live report so an edit to the report (shape, name, description) shows on the
  // tile. Pipeline reports compile their op-chain the same way the detail page
  // does; flat reports are a ViewConfig already. Fall back to the snapshot if
  // the report was deleted.
  const saved = getSavedView(config.id)
  const live = saved ?? getTemplate(config.id)
  const liveConfig = resolveTileConfig(tile)
  const result = useMemo(
    () => runQuery(liveConfig, frameForConfig(liveConfig)),
    [liveConfig]
  )
  const view = effectiveTileView(tile, liveConfig, result)
  // Resolve KPI-linked targets for display; the query above is threshold-free.
  const displayConfig = resolveThreshold(liveConfig, kpis)

  const href =
    linkTo === undefined ? `/reporting/${config.id}` : (linkTo ?? undefined)
  // A saved report is "Mine"; anything else is a shipped (Pre-built) report.
  const source = saved ? 'mine' : 'prebuilt'
  const actionNode =
    action !== undefined ? (
      action
    ) : (
      <div className="flex items-center gap-1.5">
        <SourceBadge source={source} iconOnly />
        <WidgetMenu
          items={tileMenuItems(() =>
            navigate(`/reporting/${config.id}?edit=1`)
          )}
        />
      </div>
    )

  // h-full fills the grid cell (react-grid-layout sizes the cell to the tile's
  // h×rowHeight); overflow-hidden clips any chart/table that exceeds it — the
  // full data lives on the report detail page the card links to.
  const tileName = live?.name ?? config.name
  const tileDescription = live?.description ?? config.description
  // Chart tiles center their title over the plot (a left title leaves the top
  // lopsided next to the y-axis); metric/ranked/table tiles keep it left so the
  // caption stays flush with their left-aligned bodies.
  const isChart = view !== 'metric' && view !== 'ranked' && view !== 'table'
  return (
    <DashboardCard
      href={href}
      titleAlign={isChart ? 'center' : 'left'}
      // Single-line + ellipsis: a long name shouldn't wrap and shove a metric's
      // number down. Description (else the full name) rides as a hover tooltip so
      // the truncated text is still recoverable.
      title={
        <span className="block truncate" title={tileDescription ?? tileName}>
          {tileName}
        </span>
      }
      action={actionNode}
      className="h-full overflow-hidden"
      bodyClassName="min-h-0 overflow-hidden"
    >
      <TileBody config={displayConfig} result={result} view={view} />
    </DashboardCard>
  )
}
